import React, { useEffect } from "react";
import { ExternalLink, ImageOff, X } from "lucide-react";
import { Chip } from "../ui/Badge";
import { EM_DASH, humanize } from "../../lib/format";
import { indianTrademarkApi } from "../../api/indianTrademarkApi";

function Field({ label, children, span = false }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <dt className="text-micro font-semibold uppercase tracking-wide text-ink-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-ink-900">{children ?? EM_DASH}</dd>
    </div>
  );
}

/** "applicantName" -> "Applicant Name". humanize() in lib/format.js only handles snake_case/ALL-CAPS, not this. */
function humanizeKey(key) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * `applicant` has no fixed shape — it's whatever the importer put there
 * (see the model) — so instead of naming specific fields, this just
 * renders every key it actually has, humanized.
 */
function ApplicantFields({ applicant }) {
  const entries =
    applicant && typeof applicant === "object" ? Object.entries(applicant) : [];
  if (!entries.length)
    return <p className="text-sm text-ink-400">Not published</p>;

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
      {entries.map(([key, value]) => (
        <Field key={key} label={humanizeKey(key)}>
          {value == null || value === "" ? EM_DASH : String(value)}
        </Field>
      ))}
    </dl>
  );
}

export default function IndianTrademarkDetailModal({ row, onClose }) {
  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!row) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={row.markText || `Application ${row.applicationNumber}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/75 p-4 backdrop-blur-sm sm:p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-soft bg-white shadow-float"
      >
        <div className="flex items-start gap-4 border-b border-ink-100 px-6 py-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-ink-200 bg-paper">
            {row.logo ? (
              <img
                src={indianTrademarkApi.logoUrl(row._id)}
                alt={row.markText || "Mark"}
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <ImageOff className="h-5 w-5 text-ink-300" aria-hidden="true" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl font-semibold text-ink-900">
              {row.markText || row.markType || "Device mark"}
            </h2>
            <p className="mt-0.5 text-sm text-ink-500">
              Application {row.applicationNumber}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Chip>Class {row.class ?? EM_DASH}</Chip>
              {row.jurisdiction && <Chip>{row.jurisdiction}</Chip>}
              {row.markType && <Chip>{humanize(row.markType)}</Chip>}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          <section className="mb-5">
            <h3 className="mb-2.5 text-micro font-semibold uppercase tracking-wide text-ink-500">
              Filing &amp; publication
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 rounded border border-ink-100 bg-paper/50 p-4">
              <Field label="Date of filing">{row.dateOfFiling}</Field>
              <Field label="Used since">{row.usedSince}</Field>
              <Field label="Journal number">{row.journalNumber}</Field>
              <Field label="Journal publication date">
                {row.journalPublicationDate}
              </Field>
              <Field label="Jurisdiction">{row.jurisdiction}</Field>
              <Field label="Mark type">
                {row.markType ? humanize(row.markType) : null}
              </Field>
            </dl>
          </section>

          <section className="mb-5">
            <h3 className="mb-2.5 text-micro font-semibold uppercase tracking-wide text-ink-500">
              Goods &amp; services
            </h3>
            <p className="rounded border border-ink-100 bg-paper/50 p-4 text-sm leading-relaxed text-ink-800">
              {row.goodsDescription || EM_DASH}
            </p>
          </section>

          {row.conditionAndRestriction && (
            <section className="mb-5">
              <h3 className="mb-2.5 text-micro font-semibold uppercase tracking-wide text-ink-500">
                Condition &amp; restriction
              </h3>
              <p className="rounded border border-ink-100 bg-paper/50 p-4 text-sm leading-relaxed text-ink-800">
                {row.conditionAndRestriction}
              </p>
            </section>
          )}

          <section className="mb-5">
            <h3 className="mb-2.5 text-micro font-semibold uppercase tracking-wide text-ink-500">
              Applicant
            </h3>
            <div className="rounded border border-ink-100 bg-paper/50 p-4">
              <ApplicantFields applicant={row.applicant} />
            </div>
          </section>

          {row.sourcePdf && (
            <p className="text-xs text-ink-400">
              Source: {row.sourcePdf}
              {row.sourcePageStart && (
                <>
                  {" "}
                  · page{row.sourcePageStart === row.sourcePageEnd
                    ? ""
                    : "s"}{" "}
                  {row.sourcePageStart}
                  {row.sourcePageEnd &&
                  row.sourcePageEnd !== row.sourcePageStart
                    ? `–${row.sourcePageEnd}`
                    : ""}
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-ink-100 px-6 py-4">
          <button
            type="button"
            onClick={() =>
              window.open(
                indianTrademarkApi.logoUrl(row._id),
                "_blank",
                "noopener,noreferrer",
              )
            }
            disabled={!row.logo}
            className="inline-flex h-9 items-center gap-1.5 rounded border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 hover:border-ink-300 hover:bg-ink-100/60 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            View full image
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded bg-ink-900 px-4 text-sm font-semibold text-white hover:bg-ink-800 active:bg-ink-950"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
